import axios from "axios";

import { coreService } from "../../../services";

const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB chunks

const createUploadError = (error: any, context: string): UploadError => {
  const statusCode =
    error?.statusCode || error?.response?.status || error?.status;

  const message =
    error?.message ||
    error?.error ||
    error?.response?.data?.message ||
    "Upload failed";
  const details = error?.response?.data || error?.details;

  return {
    code: `UPLOAD_${context.toUpperCase()}_ERROR`,
    message: `${message}`,
    statusCode,
    details,
  };
};

export interface MultipartUploadProgress {
  totalProgress: number;
  currentPart: number;
  totalParts: number;
  uploadedParts: number;
  failedParts: number;
  isComplete: boolean;
  error?: string;
}

export interface MultipartUploadPart {
  partNumber: number;
  etag: string;
  size: number;
}

export interface UploadError {
  code: string;
  message: string;
  statusCode?: number;
  details?: any;
}

export interface MultipartUploadResult {
  success: boolean;
  parts?: MultipartUploadPart[];
  error?: UploadError;
  upload_id?: string;
  key?: string;
}

export interface PresignedUrlResponse {
  urls?: string[];
  upload_id?: string;
  key?: string;
  upload_type?: "single" | "multipart";
  etags?: Array<{ part_number: number; etag: string }>;
}

const uploadFileToS3 = async (
  file: File,
  preSignedUrl: string,
  onUploadProgress?: (progressEvent: ProgressEvent) => void,
) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = async () => {
      const buffer = fileReader.result;
      const res = await coreService.httpClient.put(preSignedUrl, buffer, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        withCredentials: false,
        onUploadProgress,
      });

      resolve(res);
    };

    fileReader.onerror = (e) => {
      reject(createUploadError(e, "file reading"));
    };

    fileReader.readAsArrayBuffer(file);
  });
};

// Multipart upload functions
const shouldUseMultipartUpload = (fileSize: number): boolean => {
  return fileSize > CHUNK_SIZE;
};

const uploadPart = async (
  file: File,
  partNumber: number,
  start: number,
  end: number,
  presignedUrl: string,
  onPartProgress?: (progress: number) => void,
): Promise<MultipartUploadPart> => {
  const chunk = file.slice(start, end);

  await axios.put(presignedUrl, chunk, {
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    onUploadProgress: (progressEvent) => {
      if (onPartProgress && progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        onPartProgress(progress);
      }
    },
  });

  return {
    partNumber,
    etag: "",
    size: chunk.size,
  };
};

const getPartsPresignedUrls = async (
  formData: Record<string, any>,
  file: File,
  totalParts: number,
): Promise<PresignedUrlResponse> => {
  const response = await coreService.httpClient.post(
    "/episodes/pre-signed-url",
    {
      ...formData,
      duration_in_seconds: (file as any).duration_in_seconds || 0,
      total_parts: totalParts,
      file_size: file.size,
      content_type: file.type,
    },
    true,
  );
  return response.data;
};

const createPartsUploadPromises = async (
  file: File,
  totalParts: number,
  presignedData: PresignedUrlResponse,
  onProgress?: (progress: MultipartUploadProgress) => void,
) => {
  const parts: MultipartUploadPart[] = [];
  const completedParts = new Set<number>();
  const partProgress = new Map<number, number>(); // Track progress for each part (0-100)
  let failedParts = 0;

  const uploadPromises = Array.from(
    { length: totalParts },
    async (_, index) => {
      const partNumber = index + 1;
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);

      try {
        const uploadUrl = presignedData.urls![partNumber - 1];
        const part = await uploadPart(
          file,
          partNumber,
          start,
          end,
          uploadUrl,
          (progress) => {
            // Update progress for this specific part
            partProgress.set(partNumber, progress);

            // Calculate total progress across all parts
            let totalProgressSum = 0;
            for (let i = 1; i <= totalParts; i += 1) {
              if (completedParts.has(i)) {
                totalProgressSum += 100; // Completed part = 100%
              } else {
                totalProgressSum += partProgress.get(i) || 0; // In-progress part = current progress
              }
            }

            const totalProgress = Math.round(totalProgressSum / totalParts);

            if (onProgress) {
              onProgress({
                totalProgress,
                currentPart: partNumber,
                totalParts,
                uploadedParts: completedParts.size,
                failedParts,
                isComplete: false,
              });
            }
          },
        );

        // Mark this part as completed
        completedParts.add(partNumber);
        partProgress.set(partNumber, 100);
        parts.push(part);

        return part;
      } catch (error) {
        failedParts += 1;
        throw error;
      }
    },
  );

  return { uploadPromises, parts, completedParts, failedParts };
};

const uploadFileWithMultipart = async (
  file: File,
  formData: Record<string, any>,
  onProgress?: (progress: MultipartUploadProgress) => void,
): Promise<MultipartUploadResult> => {
  // Calculate parts
  const totalParts = Math.ceil(file.size / CHUNK_SIZE);

  // Get all presigned URLs in one request
  const presignedData = await getPartsPresignedUrls(formData, file, totalParts);

  if (
    !presignedData?.urls ||
    presignedData?.urls.length === 0 ||
    presignedData?.urls.length !== totalParts
  ) {
    throw new Error("Invalid presigned URLs received from server");
  }

  // Store upload details for completion
  const { upload_id: uploadId, key } = presignedData;

  const { uploadPromises, parts, completedParts, failedParts } =
    await createPartsUploadPromises(
      file,
      totalParts,
      presignedData,
      onProgress,
    );

  // Upload all parts in parallel
  await Promise.all(uploadPromises);

  // Get presigned URL for completing multipart upload
  await coreService.httpClient.post(
    "/episodes/complete-multipart-upload",
    {
      ...formData,
      upload_id: uploadId,
      duration_in_seconds: 0,
      file_size: file.size,
      key,
      is_completion: true,
    },
    true,
  );

  if (onProgress) {
    onProgress({
      totalProgress: 100,
      currentPart: totalParts,
      totalParts,
      uploadedParts: completedParts.size,
      failedParts,
      isComplete: true,
    });
  }

  return {
    success: true,
    parts,
    upload_id: uploadId,
    key,
  };
};

const uploadFile = async (
  file: File,
  formData: Record<string, any>,
  onProgress?: (progress: MultipartUploadProgress | ProgressEvent) => void,
): Promise<MultipartUploadResult> => {
  const shouldUseMultipart = shouldUseMultipartUpload(file.size);

  if (shouldUseMultipart) {
    // Use multipart upload for large files
    return uploadFileWithMultipart(
      file,
      formData,
      onProgress as (progress: MultipartUploadProgress) => void,
    );
  }

  // Use regular upload for small files
  const response = await coreService.httpClient.post(
    "/episodes/pre-signed-url",
    {
      ...formData,
      duration_in_seconds: (file as any).duration_in_seconds || 0,
      file_size: file.size,
    },
    true,
  );

  const presignedData: PresignedUrlResponse = response.data;

  if (!presignedData.urls || presignedData.urls.length === 0) {
    throw new Error("Invalid presigned URLs received from server");
  }

  await uploadFileToS3(
    file,
    presignedData.urls[0],
    onProgress as (progressEvent: ProgressEvent) => void,
  );

  // Return consistent success response for single file upload
  return {
    success: true,
    key: presignedData.key,
  };
};

export {
  uploadFileToS3,
  uploadFileWithMultipart,
  uploadFile,
  shouldUseMultipartUpload,
};
